import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import moment from "moment-timezone";
import React, { useRef } from "react";
import { BsFillCaretDownFill } from "react-icons/bs";
import { wallMomentFromFormTimeValue } from "../../../../utils/activityTimezone";

// GMA-1234: when the caregiver toggles all-day OFF, restore the timed values
// they had before the toggle, or fall back to a canonical 9 AM → 10 AM if no
// prior values were ever set (new-activity flow). 9 AM matches the iOS app
// default and the AC sign-off in the Jira ticket.
const ALL_DAY_FALLBACK_START = "09:00";
const ALL_DAY_FALLBACK_END = "10:00";

const ActivityDateTime = ({
  formik,
  mode,
  onRepeatEndUserOverride,
  /** GMA-1228: first selectable start day in the client's IANA zone (YYYY-MM-DD). */
  clientMinWallDate,
  /** Resolved IANA zone for the selected client (`resolveActivityTimezone`). Used for today's start-time floor. */
  clientIanaZone,
}) => {
  // GMA-1234: snapshot of the timed start/end at the moment the caregiver
  // turns on All-day, so toggling it back off restores the same wall hours
  // they had before. Lives in the component (not Formik) because it is a
  // UI affordance, not part of the persisted shape.
  const lastTimedValuesRef = useRef(null);
  const isAllDay = formik.values.is_all_day === true;
  const minStartDay =
    clientMinWallDate || moment().format("YYYY-MM-DD");
  const isStartOnClientToday =
    formik.values.date_from === minStartDay;
  const nowInClientWall =
    typeof clientIanaZone === "string" && clientIanaZone.length > 0
      ? moment.tz(clientIanaZone)
      : moment();

  const enforceChronology = ({ df, dt, st, et }) => {
    const date_from = moment(df ?? formik.values.date_from, "YYYY-MM-DD");
    const date_to = moment(dt ?? formik.values.date_to, "YYYY-MM-DD");
    const start_candidate = st ?? formik.values.start_time;
    const end_candidate = et ?? formik.values.end_time;
    const start_time = wallMomentFromFormTimeValue(start_candidate);
    const end_time = wallMomentFromFormTimeValue(end_candidate);

    if (
      !start_time ||
      !end_time ||
      !start_time.isValid() ||
      !end_time.isValid()
    ) {
      return;
    }

    if (date_from.isAfter(date_to)) {
      formik.setFieldValue("date_to", date_from.format("YYYY-MM-DD"));
    }

    if (
      date_from.isSame(date_to) &&
      start_time.clone().add(1, "minute").isAfter(end_time)
    ) {
      formik.setFieldValue("end_time", start_time.clone().add(1, "hour"));
      if (start_time.isAfter(moment("22:59", "HH:mm"))) {
        formik.setFieldValue(
          "date_to",
          date_from.clone().add(1, "day").format("YYYY-MM-DD")
        );
      }
    }
  };

  const handleAllDayToggle = (nextChecked) => {
    if (nextChecked) {
      lastTimedValuesRef.current = {
        start_time: formik.values.start_time,
        end_time: formik.values.end_time,
      };
      formik.setFieldValue("is_all_day", true);
    } else {
      const restored = lastTimedValuesRef.current;
      formik.setFieldValue("is_all_day", false);
      formik.setFieldValue(
        "start_time",
        restored?.start_time || ALL_DAY_FALLBACK_START,
      );
      formik.setFieldValue(
        "end_time",
        restored?.end_time || ALL_DAY_FALLBACK_END,
      );
    }
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ mb: 2, marginLeft: "-16px" }}
      >
        <Grid item xs={5} />
        <Grid item xs={7}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllDay}
                onChange={(e) => handleAllDayToggle(e.target.checked)}
                disabled={mode === "view"}
                inputProps={{
                  "aria-label": "All-day",
                  "data-testid": "activity-all-day-checkbox",
                }}
              />
            }
            label="All-day"
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{
          mb: 3,
          marginLeft: "-16px",
          textAlign: "center",
          alignItems: "center",
        }}
      >
        <Grid item xs={5} sx={{ textAlign: "right" }}>
          <InputLabel>Start</InputLabel>
        </Grid>
        <Grid item xs={7} sx={{ alignItems: "center" }} container spacing={2}>
          <div className="field-icon-grp activity-page-field">
            <input
              type="date"
              name="date_from"
              className="form-control date-field"
              data-testid="activity-date-from-input"
              aria-invalid={
                !!(formik.errors.date_from &&
                  (formik.touched.date_from || formik.submitCount > 0))
              }
              aria-describedby={
                formik.errors.date_from ? "activity-date-from-error" : undefined
              }
              min={minStartDay}
              value={formik.values.date_from}
              onChange={(e) => {
                formik.setFieldValue("date_from", e.target.value);
                enforceChronology({ df: e.target.value });
              }}
              readOnly={mode === "view"}
              disabled={mode === "view"}
            />
          </div>

          {!isAllDay && (
            <>
              <Grid item xs={1}>
                <InputLabel>at</InputLabel>
              </Grid>

              <div className="field-icon-grp activity-page-field">
                <TimePicker
                  minTime={
                    isStartOnClientToday
                      ? nowInClientWall.clone().startOf("minute")
                      : moment().startOf("day")
                  }
                  value={wallMomentFromFormTimeValue(formik.values.start_time)}
                  onChange={(newValue) => {
                    formik.setFieldValue("start_time", newValue);
                    enforceChronology({ st: newValue });
                  }}
                  readOnly={mode === "view"}
                  disabled={mode === "view"}
                  timeSteps={{ minutes: 1 }}
                  slots={{
                    textField: TextField,
                  }}
                />
              </div>
              {((formik.touched.start_time && formik.errors.start_time) ||
                (formik.submitCount > 0 && formik.errors.start_time)) && (
                <div
                  className="date-time-error"
                  data-testid="activity-start-time-error"
                >
                  {formik.errors.start_time}
                </div>
              )}
            </>
          )}
        </Grid>
      </Grid>
      {(formik.touched.date_from || formik.submitCount > 0) &&
        formik.errors.date_from && (
          <div
            className="date-time-error"
            id="activity-date-from-error"
            data-testid="activity-date-from-error"
            role="alert"
            style={{ marginLeft: "calc(41.666% - 8px)", marginBottom: "8px" }}
          >
            {formik.errors.date_from}
          </div>
        )}
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{
          mb: 3,
          marginLeft: "-16px",
          textAlign: "center",
          alignItems: "center",
        }}
      >
        <Grid item xs={5} sx={{ textAlign: "right" }}>
          {/* GMA-1228: relabel from "End" to "Activity ends" so the
              single-occurrence end is distinguishable from the recurrence
              end ("Repeats until") below. */}
          <InputLabel>Activity ends</InputLabel>
        </Grid>
        <Grid item xs={7} sx={{ alignItems: "center" }} container spacing={2}>
          <div className="field-icon-grp activity-page-field">
            <span className="field-icon">
              <BsFillCaretDownFill />
            </span>
            <input
              type="date"
              name="date_to"
              className="form-control date-field"
              onKeyDown={(e) => e.preventDefault()}
              min={moment(formik.values.date_from).format("YYYY-MM-DD")}
              value={formik.values.date_to}
              onChange={(e) => {
                formik.setFieldValue("date_to", e.target.value);
                enforceChronology({ dt: e.target.value });
              }}
              readOnly={mode === "view"}
              disabled={mode === "view"}
            />
          </div>

          {!isAllDay && (
            <>
              <Grid item xs={1}>
                <InputLabel>at</InputLabel>
              </Grid>

              <div className="field-icon-grp activity-page-field">
                <TimePicker
                  minTime={
                    moment(formik.values.date_from).isSame(
                      moment(formik.values.date_to)
                    )
                      ? wallMomentFromFormTimeValue(formik.values.start_time)
                          ?.clone()
                          .add(1, "minute") ?? moment().startOf("day")
                      : moment().startOf("day")
                  }
                  value={wallMomentFromFormTimeValue(formik.values.end_time)}
                  onChange={(newValue) => {
                    formik.setFieldValue("end_time", newValue);
                    enforceChronology({ et: newValue });
                  }}
                  readOnly={mode === "view"}
                  disabled={mode === "view"}
                  timeSteps={{ minutes: 1 }}
                  slots={{
                    textField: TextField,
                  }}
                />
              </div>
              {formik.touched.end_time && formik.errors.end_time && (
                <div className="date-time-error">{formik.errors.end_time}</div>
              )}
            </>
          )}
          {formik.touched.date_to && formik.errors.date_to && (
            <div className="date-time-error">{formik.errors.date_to}</div>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={5} sx={{ textAlign: "right" }}>
          <InputLabel>Repeat</InputLabel>
        </Grid>
        <Grid item xs={7}>
          <FormControl fullWidth>
            <Select
              inputProps={{
                "data-testid": "activity-repeat-type-select",
              }}
              value={formik.values.repeat_type}
              disabled={mode === "view"}
              onChange={(e) => {
                const newValue = e.target.value;
                const prevType = formik.values.repeat_type;
                formik.setFieldValue("repeat_type", newValue);
                if (newValue === "none" || newValue === "daily") {
                  formik.setFieldValue("repeat_days", []);
                } else if (newValue === "weekly") {
                  const prevDays = formik.values.repeat_days;
                  const needsDefault =
                    prevType !== "weekly" ||
                    !Array.isArray(prevDays) ||
                    prevDays.length === 0;
                  if (needsDefault) {
                    // GMA-1233: in the new-activity flow `date_from` can
                    // initially be "", which makes moment("", "YYYY-MM-DD")
                    // invalid and `.day()` returns NaN. Fall back to
                    // minStartDay (client today / earliest selectable day).
                    const candidate = moment(
                      formik.values.date_from,
                      "YYYY-MM-DD",
                      true,
                    );
                    const fallback = moment(minStartDay, "YYYY-MM-DD", true);
                    const source = candidate.isValid() ? candidate : fallback;
                    const dow = source.isValid() ? source.day() : null;
                    if (Number.isInteger(dow)) {
                      formik.setFieldValue("repeat_days", [dow]);
                    } else {
                      formik.setFieldValue("repeat_days", []);
                    }
                  }
                }
              }}
              sx={{
                backgroundColor: "white",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }}
            >
              <MenuItem value="none">Does not repeat</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {formik.values.repeat_type && formik.values.repeat_type !== "none" && (
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={5} sx={{ textAlign: "right" }}>
            {/* GMA-1228: previously labelled "Ends" — reused the same word
                as the single-occurrence End field above and silently ran
                for a year. Now a distinct "Repeats until" label, with a
                red asterisk to match the required validation rule. */}
            <InputLabel required>Repeats until</InputLabel>
          </Grid>
          <Grid item xs={7}>
            <input
              type="date"
              name="repeat_end_date"
              className="form-control date-field"
              // GMA-1228: lower-bound on the start date (date_from), not the
              // single-occurrence end (date_to). The recurrence end only has
              // to be >= the first occurrence's start.
              min={formik.values.date_from}
              value={formik.values.repeat_end_date || ""}
              onChange={(e) => {
                if (typeof onRepeatEndUserOverride === "function") {
                  onRepeatEndUserOverride(e.target.value);
                } else {
                  formik.setFieldValue("repeat_end_date", e.target.value);
                }
              }}
              readOnly={mode === "view"}
              disabled={mode === "view"}
              aria-required="true"
            />
            {formik.errors.repeat_end_date && (
              <div className="date-time-error">
                {formik.errors.repeat_end_date}
              </div>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default ActivityDateTime;
